// TODO: Mover todos los textos estáticos a i18n (en/es.json)
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import BackButton from "../components/BackButton";
import warningYellowIcon from "@assets/icons/warning-yellow.svg?raw&inline";
import businessIcon from "@assets/icons/business.svg?raw&inline";
import type {
  IncorporationRequest,
  StripePrice,
} from "@/schemas/incorporation";
import CompanyNameStep from "../company-name-step";
import { useTranslation } from "react-i18next";
import DirectorsStep from "../directors-step";
import FinancialProfileStep from "../financial-profile-step";
import LegalAddressStep from "../legal-address-step";
import AnnualMandatoryCosts from "./AnnualMandatoryCosts";
import { CustomTooltip } from "../../CustomTooltip";

// Helper function to get unit amount in cents from Stripe price
const getUnitAmountCents = (price?: StripePrice): number => {
  if (!price) return 0;
  if (typeof price.unit_amount === "number" && price.unit_amount !== null) {
    return price.unit_amount;
  }
  if (price.unit_amount_decimal) {
    return Math.round(parseFloat(price.unit_amount_decimal));
  }
  return 0;
};

// Helper function to get the correspondence/shipping address
const getShippingAddress = (request?: IncorporationRequest): string => {
  if (!request) return "";

  const {
    correspondencia_tipo: correspondenciaTipo,
    correspondencia_direccion: correspondenciaDireccion,
  } = request;

  // New behavior based on correspondencia_tipo from LegalAddressStep
  if (correspondenciaTipo === "digital") {
    // For digital correspondence, just indicate that it's digital
    return "Digital";
  }

  if (correspondenciaTipo === "fisico") {
    // For physical correspondence, show the specific correspondence address if provided
    if (correspondenciaDireccion && correspondenciaDireccion.trim() !== "") {
      return correspondenciaDireccion;
    }
    return "";
  }

  // Backward compatibility: if correspondencia_tipo is not set, prefer a specific
  // correspondence address; otherwise return empty so the caller can show a default.
  if (correspondenciaDireccion && correspondenciaDireccion.trim() !== "") {
    return correspondenciaDireccion;
  }

  return "";
};

const PaymentSummaryStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, next } = useIncorporationWizardFormCtx();

  // Get Stripe price and path from initial data
  const incorporationStripePrice = data?.incorporationStripePrice;
  const checkoutPath = data?.path;

  // Get company name from wizard data, fallback to default
  const companyName = data?.incorporationRequest?.nombre || "TRABAJO CORP";
  const directorsCount =
    data?.incorporationContact?.filter((contact) =>
      contact.rol?.includes("director")
    ).length || 3;
  const shareholdersCount =
    data?.incorporationContact?.filter((contact) =>
      contact.rol?.includes("accionista")
    ).length || 3;

  const isDigitalDelivery =
    data?.incorporationRequest?.correspondencia_tipo === "digital";

  // Pricing - calculate from Stripe price, fallback to hardcoded value if missing
  const incorporationCostCents = getUnitAmountCents(incorporationStripePrice);
  const incorporationCost =
    incorporationCostCents > 0 ? incorporationCostCents / 100 : 1500; // Fallback to hardcoded value if Stripe price is missing
  const courierCost = 0; // Can be calculated/additional
  const total = incorporationCost + courierCost;

  // Correspondence/shipping address according to LegalAddressStep data
  const shippingAddress = getShippingAddress(data?.incorporationRequest);

  // Check if Stripe configuration is available
  const hasStripeConfig = incorporationStripePrice && checkoutPath;

  const handleActivate = () => {
    if (
      hasStripeConfig &&
      incorporationStripePrice?.id &&
      data?.incorporationRequest?.id
    ) {
      // Redirect to Stripe checkout with price_id
      window.location.href = `${checkoutPath}&price_id=${incorporationStripePrice.id}&incorporation_request_id=${data.incorporationRequest.id}`;
    } else {
      // Fallback: log error and continue with next step for development
      console.error(
        "Stripe configuration missing: incorporationStripePrice or path not found in initial data"
      );
      next();
    }
  };

  const summarySteps = [
    // "Datos de la empresa",
    typeof CompanyNameStep.title === "function"
      ? CompanyNameStep.title(t)
      : CompanyNameStep.title,
    typeof DirectorsStep.title === "function"
      ? DirectorsStep.title(t)
      : DirectorsStep.title,
    typeof FinancialProfileStep.title === "function"
      ? FinancialProfileStep.title(t)
      : FinancialProfileStep.title,
    typeof LegalAddressStep.title === "function"
      ? LegalAddressStep.title(t)
      : LegalAddressStep.title,
  ];

  return (
    <section
      className="container"
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "2rem 1rem",
      }}
    >
      <BackButton label="Volver" />

      <div className="row g-4 g-lg-5 mt-2 mx-auto" style={{ maxWidth: 1000 }}>
        {/* Left Column - Summary */}
        <div className="col-12 col-lg-6">
          <div className="d-flex flex-column gap-4">
            {/* Title */}
            <h1 className="fw-bold mb-0 size-28">
              ¡Un paso más hacia tu empresa!
            </h1>

            {/* Description */}
            <p className="text-muted size-16">
              Realiza el pago inicial para activar la verificación de identidad
              de los directores y accionistas. Una vez completada la
              verificación, tu sociedad será constituida y recibirás los
              documentos oficiales por mensajería.
            </p>

            {/* Registration Summary */}
            <div>
              <h2 className="fw-bold mb-3 size-18">Resumen del registro</h2>

              <div
                className="d-flex flex-column"
                style={{ position: "relative" }}
              >
                {summarySteps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-start"
                    style={{
                      gap: "12px",
                      position: "relative",
                      paddingBottom:
                        index < summarySteps.length - 1 ? "16px" : "0",
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          backgroundColor: "#28a745",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </div>
                      {index < summarySteps.length - 1 && (
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            top: "22px",
                            transform: "translateX(-50%)",
                            width: "2px",
                            height: "20px",
                            backgroundColor: "#28a745",
                          }}
                        />
                      )}
                    </div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "16px",
                        lineHeight: "1.5",
                        color: "#000",
                        paddingTop: "2px",
                      }}
                    >
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Warning Message */}
            <div className="d-flex align-items-start gap-2 p-3 rounded bg-body-tertiary">
              <div
                style={{
                  width: 24,
                  height: 24,
                  display: "inline-flex",
                  flexShrink: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: warningYellowIcon.replace(
                    /<svg([^>]*)>/,
                    '<svg$1 style="width: 24px; height: 24px;">'
                  ),
                }}
              />
              <p className="mb-0 size-14 text-black">
                Una vez realizado el pago, se enviarán invitaciones a los
                directores y accionistas para completar su verificación de
                identidad.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - Payment Card */}
        <div className="col-12 col-lg-6">
          <div
            className="p-4 rounded shadow-sm"
            style={{
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
            }}
          >
            <div className="d-flex flex-column gap-3">
              {/* Card Header */}
              <div className="d-flex align-items-start gap-3">
                <div
                  className="bg-info-subtle rounded p-3"
                  dangerouslySetInnerHTML={{ __html: businessIcon }}
                />
                <div className="d-flex flex-column">
                  <h3 className="fw-bold mb-1 size-18">{companyName}</h3>
                  <p className="mb-0 text-muted size-14 fst-italic">
                    {shareholdersCount} accionistas {directorsCount} directores
                    y dignatarios
                  </p>
                </div>
              </div>

              {/* Service Type */}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="text-black mb-0">
                    Constitución de S.A.
                  </p>
                  <span className="size-14">
                    ${incorporationCost.toLocaleString("en-US")}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  {isDigitalDelivery ? (
                    <>
                      <span>Envío de documentos digitales</span>
                      <span className="text-muted size-14 fst-italic">
                        Gratis
                      </span>
                    </>
                  ) : (
                    <>
                      <span>Envío de documentos físicos</span>
                      <span className="text-muted size-14 fst-italic">
                        <CustomTooltip
                          label="Costo adicional"
                          text="El costo será confirmado por nuestros abogados una vez reciban la solicitud."
                        />
                      </span>
                    </>
                  )}
                </div>
              </div>
              <hr />
              {/* Total */}
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold size-20">TOTAL</span>
                <span className="fw-bold size-20">
                  {total.toLocaleString("en-US")} US$
                </span>
              </div>

              {/* Legal Disclaimer */}
              <p className="mb-0 text-muted size-14">
                La constitución solo podrá completarse una vez verificada la
                identidad de todos los participantes, según la normativa legal
                panameña. Si alguna verificación no se completa en 30 días, te
                reembolsaremos el 90% del pago.
              </p>

              {/* CTA Button */}
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleActivate}
                disabled={!hasStripeConfig}
              >
                Activar verificación y finalizar constitución
              </button>
              <AnnualMandatoryCosts />
            </div>
          </div>
          {/* Shipping / Correspondence Address */}
          <p className="mt-1 text-center text-muted size-14 fst-italic">
            Dirección de correspondencia:{" "}
            {shippingAddress || "Proporcionada por los abogados"}
          </p>
        </div>
        {/* Platform Disclaimer */}
        <p className="mt-3 text-muted size-12">
          Panadata actúa como plataforma tecnológica que facilita la
          incorporación de sociedades en coordinación con firmas de abogados
          registradas en Panamá. La revisión legal y constitución formal son
          realizadas por la firma aliada conforme a la normativa vigente.
        </p>
      </div>
    </section>
  );
};

PaymentSummaryStep.title = "Resumen de pago";
PaymentSummaryStep.description = "Revisa el resumen y procede con el pago";

export default PaymentSummaryStep;
